import { set, get, del, keys } from 'idb-keyval';
import $api from '@/modules/request';
import { noop } from '@/modules/utils';

// NOTE: create an idb connection early on
// to workaround an issue in iOS 14.6
// see: https://developer.apple.com/forums/thread/681201
(function openIdbConnection() {
  if (!window.indexedDB) {
    console.log('warn: idb not available');
    return;
  }
  const request = window.indexedDB.open('MyTestDatabase');
  request.onerror = () => {
    console.log('Error: idb connection failed', request.errorCode);
  };
  request.onsuccess = () => {
    console.log('idb connection opened');
  };
})();

const RESOURCES = {
  video: {
    start: '/video/analyzing-start.mp4',
    loop: '/video/analyzing-loop.mp4',
    footStart: '/video/foot-start.mp4',
    footLoop: '/video/foot-loop.mp4',
  },
  gif: {
    start: '/gif/analyzing-start.gif',
    loop: '/gif/analyzing-loop.gif',
    footStart: '/gif/foot-start.gif',
    footLoop: '/gif/foot-loop.gif',
  },
};

const VERSION = '4';
const makeKey = key => `${VERSION}/${key}`;

const createObjectUrlFromBuffer = (buffer, type) => {
  const blob = new Blob([buffer], { type });
  return URL.createObjectURL(blob);
};

const SCAN_RECORDS_LIFESPAN_SECS = 120;
const isDataStale = timestamp => (Date.now() - timestamp || 0) / 1000 >= SCAN_RECORDS_LIFESPAN_SECS;

const CacheManager = {
  clearRecords(username, kind) {
    return del(makeKey(`${username}:records:${kind}`)).catch(noop);
  },
  getRecords(username, kind) {
    const key = makeKey(`${username}:records:${kind}`);
    return get(key)
      .then(data => {
        const { timestamp = 0, records } = data || {};
        return isDataStale(timestamp) ? false : records;
      })
      .catch(() => false);
  },
  setRecords(username, kind, records) {
    return set(makeKey(`${username}:records:${kind}`), {
      timestamp: Date.now(),
      records,
    }).catch(noop);
  },
  getFreshMobileScan(username) {
    return get(makeKey(`${username}:mobile-scan`)).catch(noop);
  },
  setFreshMobileScan(username, record) {
    return set(makeKey(`${username}:mobile-scan`), record).catch(noop);
  },
  clearFreshMobileScan(username) {
    return del(makeKey(`${username}:mobile-scan`)).catch(noop);
  },
  clearUserStuff(username) {
    return keys()
      .then(ks => ks.filter(k => k.indexOf(username) > -1))
      .then(ks => Promise.all(ks.map(k => del(k))))
      .catch(noop);
  },
  loadAllGif() {
    return Promise.all(Object.keys(RESOURCES.gif).map(key => this.fetchGif(key)))
      .then(() => {
        console.log('CacheManager AllGif loaded');
      })
      .catch(noop);
  },
  getGifSrc(key) {
    return get(makeKey(`gif:${key}`))
      .then(buffer => {
        if (!buffer) {
          console.log(`did not find ${key} from idb`);
          return this.fetchGif(key).then(() => this.getGifSrc(key));
        }
        console.log(`found ${key} from idb`);
        return createObjectUrlFromBuffer(buffer, 'image/gif');
      })
      .catch(e => {
        console.log('Failed to get gif source: ', e.message);
        return RESOURCES.gif[key];
      });
  },
  fetchGif(key) {
    return fetch(RESOURCES.gif[key])
      .then(response => response.arrayBuffer())
      .then(buffer => set(makeKey(`gif:${key}`), buffer))
      .catch(error => {
        console.log('error setting buffer to idb', key, error);
      });
  },
  loadAllVideo() {
    return Promise.all(Object.keys(RESOURCES.video).map(key => this.fetchVideo(key)))
      .then(() => {
        console.log('CacheManager AllVideo loaded');
      })
      .catch(noop);
  },
  getVideoSrc(key) {
    return get(makeKey(`video:${key}`))
      .then(buffer => {
        if (!buffer) {
          console.log(`did not find ${key} from idb`);
          return this.fetchVideo(key).then(() => this.getVideoSrc(key));
        }
        console.log(`found ${key} from idb`);
        return createObjectUrlFromBuffer(buffer, 'video/mp4');
      })
      .catch(e => {
        console.log('Failed to get video source: ', e.message);
        return RESOURCES.video[key];
      });
  },
  fetchVideo(key) {
    return fetch(RESOURCES.video[key])
      .then(response => response.arrayBuffer())
      .then(buffer => set(makeKey(`video:${key}`), buffer))
      .catch(error => {
        console.log('error setting buffer to idb', key, error);
      });
  },
  async loadTgs(tid) {
    // return a cached tgs url from idb or fetch from remote and store it
    try {
      let data = await get(makeKey(`tgs:${tid}`)).catch(noop);
      if (!data) {
        console.log('tgs not found in cache. fetching...');
        const resp = await $api.user.get(`/api/v1/scan_records/${tid}/tgs`, {
          responseType: 'arraybuffer',
        });
        data = resp.data;
        await set(makeKey(`tgs:${tid}`), data).catch(noop);
      } else {
        console.log('tgs found in cache');
      }
      return createObjectUrlFromBuffer(data, 'application/octet-stream');
    } catch (e) {
      console.log(e.message);
      return '';
    }
  },
};
export default CacheManager;
